import Backbone, {ViewOptions} from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence as ComponentSequence} from 'components/Sequence';
import * as vo from 'components/VisibilityOptions';
import {VisibilityOptionSettings} from 'components/VisibilityOptions';
import DNASearchExamples from 'molbio/biological-search/DNASearchExamples';
import FeatureResultTemplate from 'molbio/biological-search/FeatureResultTemplate';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import {FeatureSearchResultFeature} from 'molbio/biological-search/SearchModels';
import {PART_ANNOTATION_COLOR} from 'molbio/maps/PartsViewConstants';
import SettingsModal, {SETTINGS_MODAL_KEY} from 'molbio/maps/SettingsModal';
import {SearchSequenceAdvancedOptions, SequenceSearchType} from 'molbio/SearchSequenceAdvancedOptions';
import {trackSequenceMapCtrlF} from 'molbio/SequenceAnalyticsHelper';
import * as sequtil from 'molbio/SeqUtil';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import excess from 'notebook/tables/excess';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {NumberTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import * as ModalContainer from 'ui-library/ModalContainer';
import * as keyboard from 'util/Keyboard';

function getFeatureColor(feature: FeatureSearchResultFeature): string {
  if (feature.type() === 'part' && BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
    return PART_ANNOTATION_COLOR;
  }
  return feature.color();
}

const searchDNAData: SearchDNAData = SearchDNAData.get();

class SearchDNAView extends Backbone.View<SearchDNAData> {
  static el: string;

  focused!: Element | null;

  static initClass(): void {
    this.prototype.el = '#seqsearch';
  }

  events(): {[e: string]: string | (() => void)} {
    return {
      'click .feature-result a': 'clickFeature',
      'mousedown .js-adv-settings': 'toggleSettings',
      'mousedown .js-close-adv-settings': 'toggleSettings',
      'change [name=search-region]': 'updateSearchRegion',
      'change [name=search-target]': 'updateSearchTarget',
      'change [name=mismatches]': () => {
        this.doSearch();
      },
      'click .js-orf-settings': 'showORFSettings',
    };
  }

  handleKeyup(e: KeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      this.$('.find-next-btn').removeClass('active');
      this.$('.find-previous-btn').removeClass('active');
    }
  }

  inputSearch(q: string | null = null): void {
    if (q != null) {
      this.$('.seq-search-box').val(q);
      this.$('.seq-search-box').select();
      this.$('.seq-search-box').trigger('input');
    } else {
      this.$('.seq-search-box').select();
    }
  }

  startIndex(): number {
    const sequence = this.model.get('sequence');
    assert(sequence != null, 'Expected sequence to be present when calling SearchDNAView.startIndex().');
    return sequence.startIndex();
  }

  inputGoto(start: number, end: number | null): void {
    start = sequtil.looseModelToDisplay(start, this.startIndex());
    const q = (() => {
      if (end != null) {
        end = sequtil.looseModelEndToDisplay(end, this.startIndex());
        return `:${start}:${end}`;
      } else if (start != null) {
        return `:${start}`;
      } else {
        return ':';
      }
    })();
    this.$('.seq-search-box').val(q);
    this.$('.seq-search-box').select();
    (this.$('.seq-search-box')[0] as HTMLInputElement).selectionStart = 1;
    (this.$('.seq-search-box')[0] as HTMLInputElement).selectionEnd = q.length;

    if (q.length > 1) {
      this.$('.seq-search-box').trigger('input');
    }
  }

  focusSearch(): void {
    this.focused = document.activeElement;
    this.$('.seq-search-box').focus();
  }

  cancelSearch(): void {
    if (searchDNAData.get('mode') === 'gotobase') {
      this.restoreBase();
    }
    this.closeSearch();
  }

  closeSearch(): void {
    // Return focus to whoever had it.
    if (this.focused == null || this.focused === this.$('.seq-search-box')[0]) {
      this.$('.seq-search-box').blur();
    } else {
      $(this.focused).focus();
    }

    // Hide box.
    searchDNAData.resetSearch();
    this.$el.animate({top: '-180px'}, 200);

    searchDNAData.set('mode', null);
  }

  openSearch(): void {
    this.focusSearch();
    this.inputSearch('');
    searchDNAData.set('mode', 'search');
  }

  _searchViewIsVisible(): boolean {
    if ($('#editor .modal-backdrop').length !== 0) {
      return false;
    }
    if (FullScreenStore.getCurrentScreen() != null) {
      return false;
    }
    if (SidepanelStore.getIsExpanded()) {
      return false;
    }
    return true;
  }

  handleKeydown(e: KeyboardEvent): void {
    if (!this._searchViewIsVisible()) {
      return;
    }

    // Handle keydown for sequence only if a sequence is open
    if (searchDNAData.get('sequence') != null) {
      this.handleKeydownForSequence(e);
    }

    // Otherwise, we let the browser handle the event (which renders the built-in search box)
  }

  _getEvalVars(): {c: number; e?: number} {
    return {
      // 'gotoFromBase' may actually be null. However, it is not safe to add an assert statement here,
      // because it breaks the error handling logic downstream that calls `setGoToInvalid`, and doesn't allow
      // the expression to be re-evaluated as valid again.
      c: sequtil.looseModelToDisplay(searchDNAData.get('gotoFromBase')!, this.startIndex()),
      e:
        searchDNAData.get('gotoFromBaseEnd') != null
          ? sequtil.looseModelEndToDisplay(searchDNAData.get('gotoFromBaseEnd')!, this.startIndex())
          : undefined,
    };
  }

  /**
   * Evaluates an arithmetic expression allowing Excel syntax, where `c` and `e` are variables that
   * correspond to the start and end of the initial selection. Throws for invalid expressions.
   * @param  {String} expression  e.g.: '1+2/c'
   * @return {Number}
   */
  _evaluateExpression(expression: string): number {
    // Create an Excel formula where 'c' and 'e' values are to be located in cells A1 and A2 respectively.
    const sanitizedExpression = `=${expression.replace(/\bc\b/g, 'A1').replace(/\be\b/g, 'A2')}`;

    // Create environment where A1:=c, and A2:=e
    const {c, e} = this._getEvalVars();
    const cVal = c != null ? excess.util.astValue({rawValue: c, type: BaseTypedValueType.NUMBER}) : null;
    const eVal = e != null ? excess.util.astValue({rawValue: e, type: BaseTypedValueType.NUMBER}) : null;
    const env = new excess.Environment();
    env.currentTable = 'SearchBox';
    const cValueWithError =
      cVal == null
        ? {value: cVal, error: new excess.util.ReferenceError(''), locked: false}
        : {value: cVal, error: null, locked: false};
    const eValueWithError =
      eVal == null
        ? {value: eVal, error: new excess.util.ReferenceError(''), locked: false}
        : {value: eVal, error: null, locked: false};
    env.put({row: 0, column: 0, table: env.currentTable}, cValueWithError);
    env.put({row: 1, column: 0, table: env.currentTable}, eValueWithError);

    // Parse excel formula and evaluate
    const node = excess.parse(sanitizedExpression);
    return (node.eval(env) as NumberTypedValue).rawValue;
  }

  handleKeydownForSequence(e: KeyboardEvent): void {
    let sel;
    let selEnd;
    if (keyboard.isCtrlG(e)) {
      this.focusSearch();

      const sequence = this.model.get('sequence');
      assert(sequence != null, 'Expected sequence to be present when calling handleKeydownForSequence.');
      sel = sequence.get('selection');
      const selStart = sel.start();
      selEnd = sel.end();

      this.inputGoto(selStart, selEnd);

      e.stopPropagation();
      e.preventDefault();
    } else if (keyboard.isCtrlF(e) || keyboard.isCtrlShiftF(e)) {
      this.focusSearch();
      searchDNAData.set({searchAminoAcids: e.shiftKey});
      // Log sequence analytics when users re-initialize a search
      if (this.focused !== this.$('.seq-search-box')[0]) {
        trackSequenceMapCtrlF(this.model.get('sequence'));
      }
      if (searchDNAData.get('mode') === 'gotobase') {
        this.inputSearch('');
        searchDNAData.set('mode', 'search');
      } else {
        this.inputSearch('');
      }

      e.stopPropagation();
      e.preventDefault();
    } else if ((keyboard.isUpKey(e) || keyboard.isDownKey(e)) && searchDNAData.get('mode') === 'gotobase') {
      // updown
      e.stopPropagation();
      e.preventDefault();

      const seq = this.model.get('sequence');
      assert(seq != null, 'Expected sequence to be present when calling handleKeydownForSequence.');

      const dir = keyboard.isUpKey(e) ? 1 : -1;

      // Find out which part of the selection we are manipulating.
      selEnd = (this.$('.seq-search-box')[0] as HTMLInputElement).selectionEnd!;
      let q = this.$('.seq-search-box').val();
      const parts = q.split(':');
      // eslint-disable-next-line no-restricted-syntax
      const part = parts.length - q.slice(_.indexOf(q, ':', selEnd)).split(':').length;
      try {
        // Compute target start and end positions.
        let targetEnd;
        let targetStart = sequtil.looseDisplayToModel(this._evaluateExpression(parts[1]), this.startIndex());
        if (part === 1) {
          targetStart += dir;
        }
        if (parts.length > 2) {
          targetEnd = sequtil.looseDisplayEndToModel(this._evaluateExpression(parts[2]), this.startIndex());
          if (part === 2) {
            targetEnd += dir;
          }
        } else {
          targetEnd = seq.length() + 1; // for boundary conditions
        }

        if (part === 1) {
          if (!seq.circular()) {
            targetStart = Math.min(Math.max(0, targetStart), targetEnd - 1);
          }
          parts[part] = String(sequtil.looseModelToDisplay(targetStart, this.startIndex()));
          q = parts.join(':');
          this.$('.seq-search-box').val(q);
          const startStart = parts[0].length + 1;
          (this.$('.seq-search-box')[0] as HTMLInputElement).selectionStart = startStart;
          (this.$('.seq-search-box')[0] as HTMLInputElement).selectionEnd = startStart + parts[1].length;
          this.doSearch('start'); // move cursor to start!
        } else if (part === 2) {
          // modify end
          if (!seq.circular()) {
            targetEnd = Math.max(targetStart + 1, Math.min(seq.length(), targetEnd));
          }
          parts[part] = String(sequtil.looseModelEndToDisplay(targetEnd, this.startIndex()));
          q = parts.join(':');
          this.$('.seq-search-box').val(q);
          const endStart = parts[0].length + 1 + parts[1].length + 1;
          (this.$('.seq-search-box')[0] as HTMLInputElement).selectionStart = endStart;
          (this.$('.seq-search-box')[0] as HTMLInputElement).selectionEnd = endStart + parts[2].length;
          this.doSearch('end'); // move cursor to end!
        } else {
          return;
        }
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (error) {
        this.setGotoInvalid();
      }
    } else if (keyboard.isEnterKey(e) && this.$('.seq-search-box').is(':focus')) {
      if (searchDNAData.get('mode') === 'gotobase') {
        this.doSearch();
        this.closeSearch();
      } else {
        const target = e.shiftKey ? this.$('.find-previous-btn') : this.$('.find-next-btn');
        target.click();
        target.addClass('active');
      }

      e.stopPropagation();
      e.preventDefault();
    } else if (keyboard.isEscapeKey(e) && this.$('.seq-search-box').is(':focus')) {
      e.stopPropagation();
      e.preventDefault();
      this.cancelSearch();
    }
  }

  update(seq: ComponentSequence | null): void {
    this.model.setSequence(seq);
  }

  jumpToResult(): void {
    const index = searchDNAData.get('index');
    if (index > -1) {
      const res = this.model.get('results').at(index);
      if (res) {
        res.get('sequence').trigger('jump', res.start(), null, res.end());
      }
    }
  }

  restoreBase(): void {
    const seq = this.model.get('sequence');
    assert(seq != null, 'Expected sequence to be present when calling restoreBase.');
    const sel = seq.get('selection');
    const target = searchDNAData.get('gotoFromBase');
    const targetEnd = searchDNAData.get('gotoFromBaseEnd');
    if (targetEnd != null) {
      seq.trigger('jump', target, null, targetEnd);
      sel.setEndpoints(target, targetEnd);
    } else if (target != null) {
      seq.trigger('jump', target);
      sel.reset(target);
    }
  }

  storeBase(): void {
    const seq = this.model.get('sequence');
    assert(seq != null, 'Expected sequence to be present when calling storeBase.');
    const sel = seq.get('selection');
    searchDNAData.set('gotoFromBase', sel.start());
    searchDNAData.set('gotoFromBaseEnd', sel.end());
  }

  setGotoInvalid(): void {
    this.$('.seq-search-box').css('border-color', 'red');
    searchDNAData.set('gotoMsg', 'invalid expr.');
  }

  doSearch(jumpTo?: string): boolean | SearchDNAData {
    if (jumpTo == null) {
      jumpTo = 'start';
    }
    this.$('.seq-search-box').css('border', '');

    const query = this.$('.seq-search-box').val();
    let maxMismatches = searchDNAData.get('maxMismatches');
    maxMismatches = maxMismatches && maxMismatches > 0 ? maxMismatches : 0;

    const seq = this.model.get('sequence');
    assert(seq != null, 'Expected sequence to be present when calling doSearch.');
    const sel = seq.get('selection');

    // If we are no longer searching, reset base.
    if (
      (query.length === 0 || query.charAt(0) !== ':' || query === ':') &&
      searchDNAData.get('mode') === 'gotobase'
    ) {
      this.restoreBase();
    } else if (query.length > 0 && query.charAt(0) === ':' && searchDNAData.get('mode') !== 'gotobase') {
      this.storeBase();
    }

    if (query.length === 0) {
      this.$('.seq-search-count').text('');
      searchDNAData.resetSearch();
      return searchDNAData.set('mode', 'search');
    } else {
      if (query.charAt(0) === ':') {
        searchDNAData.set('mode', 'gotobase');
        searchDNAData.resetSearch();

        if (query.split(':').length > 3) {
          this.setGotoInvalid();
          return false;
        }

        if (query.length > 1) {
          let circular;
          let displayStart;
          let match;
          let targetEnd;
          let targetStart;
          if (!query.match(/^:([^:]+)(:([^:]+)?)?$/)) {
            this.setGotoInvalid();
            return false;
          }
          try {
            match = query.match(/:([^:]+)/)[1];
            targetStart = sequtil.looseDisplayToModel(this._evaluateExpression(match), this.startIndex());
            // eslint-disable-next-line benchlint/always-throw-from-catch
          } catch (error1) {
            this.setGotoInvalid();
            return false;
          }

          if (typeof targetStart !== 'number') {
            this.setGotoInvalid();
            return false;
          }

          const endMatch = query.match(/:[^:]+:([^:]+)/);
          if (endMatch) {
            try {
              targetEnd = sequtil.looseDisplayEndToModel(
                this._evaluateExpression(endMatch[1]),
                this.startIndex()
              );
              // eslint-disable-next-line benchlint/always-throw-from-catch
            } catch (error2) {
              this.setGotoInvalid();
              return false;
            }

            if (typeof targetEnd !== 'number') {
              this.setGotoInvalid();
              return false;
            }
          }

          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          const inRange = (num: number) => {
            circular = seq.circular();
            return (
              (circular && -seq.length() <= num && num < 2 * seq.length()) ||
              (!circular && -seq.length() <= num && num <= seq.length())
            );
          };

          if (!inRange(targetStart) || (targetEnd != null && !inRange(targetEnd))) {
            this.$('.seq-search-box').css('border-color', 'red');

            const sequenceStart = sequtil.modelToDisplay(0, this.startIndex());
            const sequenceEnd = sequtil.modelToDisplay(seq.length() - 1, this.startIndex());
            searchDNAData.set('gotoMsg', `must be in range ${sequenceStart} - ${sequenceEnd}`);
            return false;
          }

          if (targetEnd != null) {
            displayStart = sequtil.looseModelToDisplay(targetStart, this.startIndex());
            const displayEnd = sequtil.looseModelEndToDisplay(targetEnd, this.startIndex());
            searchDNAData.set('gotoMsg', `selection (${displayStart}, ${displayEnd})`);
          } else {
            displayStart = sequtil.looseModelToDisplay(targetStart, this.startIndex());
            searchDNAData.set('gotoMsg', `go to base (${displayStart})`);
          }
          targetStart = sequtil.modInclusive(targetStart, seq.length());
          if (targetEnd != null) {
            if (targetEnd === 0) {
              targetEnd = seq.length();
            }
            targetEnd = sequtil.modInclusive(targetEnd, seq.length());
            if (!seq.circular() && targetStart > targetEnd) {
              this.setGotoInvalid();
              return false;
            }
            sel.setEndpoints(targetStart, targetEnd);
          } else {
            sel.reset(targetStart);
          }
          if (targetEnd == null || jumpTo === 'start') {
            seq.trigger('jump', targetStart);
          } else {
            seq.trigger('jump', targetEnd);
          }
        } else {
          searchDNAData.set('gotoMsg', null);
        }
      } else {
        searchDNAData.set('mode', 'search');
        const range = !sel.isNotEmpty()
          ? null
          : {
              start: sel.start(),
              end: sel.end(),
            };
        searchDNAData.doSearch(query, range, {
          // will search the selection
          searchFeatures: true,
          maxMismatches,
        });
        if (searchDNAData.get('results').length > 0) {
          // TODO: Jump to closest result?
          searchDNAData.changeIndex(0);
        } else {
          searchDNAData.changeIndex(-1);
        }

        searchDNAData.set('hasSearched', true);
        this.jumpToResult();
      }

      return true;
    }
  }

  handleRegionChange(): void {
    this.handleBigSequence();
    this.updatePlaceHolder();
  }

  handleBigSequence(): void {
    let bigSelection;
    const sequence = searchDNAData.get('sequence');
    assert(sequence != null, 'Expected sequence to be present when calling handleBigSequence().');

    const bigSequence = sequence.isReallyBig();
    if (sequence.selection().isEmpty()) {
      bigSelection = sequence.isReallyBig();
    } else {
      bigSelection = sequence.selection().isReallyBig();
    }

    // For mismatches, we only care about the size of the selection.
    this.$('.js-search-mismatches .js-mismatches-help-enabled').toggleClass('hide', bigSelection);
    this.$('.js-search-mismatches .js-mismatches-help-disabled').toggleClass('hide', !bigSelection);
    this.$('.js-mismatches-controls').prop('disabled', bigSelection);
    this.$('.js-mismatches').prop('disabled', bigSelection);

    // We only calculate ORFs for small sequences right now, so the selection size doesn't matter.
    this.$('.js-search-region .js-region-help-enabled').toggleClass('hide', bigSequence);
    this.$('.js-search-region .js-region-help-disabled').toggleClass('hide', !bigSequence);
    this.$('#restrictToORF').prop('disabled', bigSequence);

    if (bigSequence) {
      this.$('#restrictToORF').prop('checked', false);
      searchDNAData.set('region', 'all');
    }
    if (bigSelection) {
      this.$('.js-mismatches').val('');
      searchDNAData.set('maxMismatches', 0);
    }
  }

  sequenceChangeHandler(): void {
    if (searchDNAData.previous('sequence') != null) {
      const prevSequence = searchDNAData.previous('sequence');
      this.stopListening(prevSequence.get('selection'));
      this.stopListening(prevSequence.get('_sequence'));
    }
    if (searchDNAData.get('sequence') != null) {
      const sequence = searchDNAData.get('sequence')!;
      this.listenTo(sequence.get('_sequence'), 'change:bases', () => {
        this.handleRegionChange();
      });
      this.listenTo(sequence.get('selection'), 'change:start change:end', () => {
        this.handleRegionChange();
      });
      this.handleRegionChange();
      this.$('.seq-search-box').prop('disabled', false);
    } else {
      this.$('.seq-search-box').prop('disabled', true);
      this.closeSearch();
    }
  }

  toggleSettings(e: Event): void {
    e.preventDefault(); // don't steal focus
    searchDNAData.set('showSettings', !searchDNAData.get('showSettings'));
  }

  showSettings(): void {
    const canShow = searchDNAData.get('mode') === 'search';
    const show = canShow && searchDNAData.get('showSettings');
    this.$('.settings').toggleClass('hide', !show);
    this.$('.js-adv-settings').toggleClass('active', show);

    // Target
    const targ = searchDNAData.get('searchAminoAcids') ? 'amino acids' : 'bases';
    const isAminoAcids = searchDNAData.get('searchAminoAcids');
    const basesButton = this.$('.js-search-type-bases');
    const aminosButton = this.$('.js-search-type-amino-acids');
    if (isAminoAcids) {
      this.$('.js-mismatches').val('');
      // Since we can't explicitly manipulate state within a child React component, instead we just poke it
      if (basesButton.hasClass('button--tertiaryBluePowder')) {
        aminosButton.click();
      }
    } else if (aminosButton.hasClass('button--tertiaryBluePowder')) {
      basesButton.click();
    }
    this.$('.js-search-mismatches').toggleClass('hide', isAminoAcids);
    this.$('select[name=search-target]').val(targ);

    // Region
    const region = searchDNAData.get('region');
    const isORFs = region === 'orfs';
    this.$('select[name=search-region]').val(region);
    this.$('.js-orf-settings').toggleClass('hide', !isORFs);
  }

  showORFSettings(): void {
    const searchingOverRNA: boolean = Boolean(searchDNAData.get('sequence')?.isRNASequence());
    const nucleotideSpecificSettings: vo.VisibilityOptionConfig = searchingOverRNA ? vo.RNA_ORFS : vo.ORFS;

    ModalContainer.open(
      SETTINGS_MODAL_KEY,
      <SettingsModal settings={nucleotideSpecificSettings.settings as VisibilityOptionSettings} />
    );
  }

  updateORFSearch(): void {
    if (searchDNAData.get('region') === 'orfs') {
      this.doSearch();
    }
  }

  updatePlaceHolder(): void {
    const sequence = searchDNAData.get('sequence');
    assert(sequence != null, 'Expected sequence to be present when calling updatePlaceHolder');
    const selection = sequence.selection();
    const targ = searchDNAData.get('searchAminoAcids') ? 'amino acids' : 'bases';
    if (!selection.isNotEmpty()) {
      this.$('.seq-search-box').attr('placeholder', `Search ${targ}, annotations, translations, and primers`);
      setTestAttrs(this.$('.seq-search-box'), {component: 'SearchDNAView', key: 'searchInput'});
    } else {
      const start = sequtil.modelStartToDisplay(selection.start(), this.startIndex());
      const end = sequtil.modelEndToDisplay(selection.end(), sequence.length(), this.startIndex());
      this.$('.seq-search-box').attr('placeholder', `Search ${targ} in selection (${start}-${end})`);
    }
  }

  showFeatureResults(): void {
    const results = searchDNAData.featureResults();
    const $ul = this.$('.search-dropdown ul');
    $ul.empty();
    if (searchDNAData.get('featCapped')) {
      $ul.append(`\
<li class='result-msg'>
  <span class='result-count'>${results.length}+ feature results</span>
  <span class='result-info'>Refine search to see all results</span>
</li>`);
    } else {
      $ul.append(`\
<li class='result-msg'>
  <span class="result-count">${results.length} feature results</span>
</li>`);
    }
    setTestAttrs(this.$('.search-dropdown ul'), {component: 'SearchDNAView', key: 'featureCount'});

    results.each((r) => {
      const f = r.feature();
      const name = [
        f.name().slice(0, r.start()),
        f.name().slice(r.start(), r.end()),
        f.name().slice(r.end()),
      ];
      const strand = (() => {
        switch (f.strand()) {
          case 1:
            return 'FWD';
          case -1:
            return 'REV';
          default:
            return '';
        }
      })();
      const start = sequtil.modelToDisplay(f.start(), this.startIndex());
      const end = sequtil.modelEndToDisplay(
        f.end(),
        searchDNAData.get('sequence')!.length(),
        this.startIndex()
      );

      // eslint-disable-next-line new-cap
      const $li = $(FeatureResultTemplate({name, strand, start, end}));
      $li.find('a').data('feature', f);
      $li
        .find('.feat-swatch')
        .css('background-color', getFeatureColor(f))
        .toggleClass('striped', f.type() === 'primer');
      $ul.append($li);
    });

    this.$('.search-dropdown').toggleClass('open', results.length > 0);
  }

  clickFeature(e: MouseEvent): void {
    e.preventDefault();
    const f = $(e.currentTarget!).data('feature');
    f.click(this.model.get('sequence'));
    this.$('.search-dropdown').removeClass('open');
    this.closeSearch();
  }

  renderAdvancedOptions(): void {
    renderToDOM(
      <SearchSequenceAdvancedOptions
        searchType={
          searchDNAData.get('searchAminoAcids') ? SequenceSearchType.AMINO_ACIDS : SequenceSearchType.BASES
        }
        onChangeSearchType={(searchType: SequenceSearchType) => {
          searchDNAData.set('searchAminoAcids', searchType === SequenceSearchType.AMINO_ACIDS);
        }}
        searchRegion={searchDNAData.get('region')}
        onChangeSearchRegion={(region: string) => {
          searchDNAData.set('region', region);
        }}
        mismatchesAllowed={searchDNAData.get('maxMismatches')}
        onChangeMismatchesAllowed={(mismatches: number | null) => {
          const nonNegativeMismatches = mismatches && mismatches >= 0 ? mismatches : 0;
          searchDNAData.set('maxMismatches', nonNegativeMismatches);
        }}
      />,
      this.$('.settings')[0]
    );
  }

  initialize(options: ViewOptions<SearchDNAData>): void {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.focused = null;
    $(window).keydown((e: KeyboardEvent) => {
      this.handleKeydown(e);
    });
    $(window).keyup((e: KeyboardEvent) => {
      this.handleKeyup(e);
    });

    this.$('.js-ctrl-f-help.glyphicon-question-sign').popover({
      placement: 'bottom',
      title: '<b>Examples</b>',
      trigger: 'hover',
      content: ReactDOMServer.renderToStaticMarkup(<DNASearchExamples />),
      html: true,
      delay: 100,
      container: 'body',
    });

    this.renderAdvancedOptions();

    this.$('.seq-search-box').bind('input', (e) => {
      if (!this.model.has('sequence')) {
        return;
      }
      this.doSearch();
    });

    this.$('.seq-search-box').blur(() => {
      this.focused = null;
    });

    this.$('.seq-search-box').focus(() => {
      this.$el.animate({top: '0px'}, 200);
    });

    this.$('.close-btn').click(() => {
      this.closeSearch();
    });

    this.$('.find-previous-btn').click(() => {
      this.$('.seq-search-box').focus();
      if (!searchDNAData.get('hasSearched')) {
        this.doSearch();
      } else {
        // If the search actually had results
        // eslint-disable-next-line no-lonely-if
        if (searchDNAData.get('index') >= 0) {
          searchDNAData.changeIndex(Math.max(0, searchDNAData.get('index') - 1));
          this.jumpToResult();
        }
      }
    });

    this.$('.find-next-btn').click(() => {
      this.$('.seq-search-box').focus();
      if (!searchDNAData.get('hasSearched')) {
        this.doSearch();
      } else {
        searchDNAData.changeIndex(
          Math.min(searchDNAData.get('results').length - 1, searchDNAData.get('index') + 1)
        );
        this.jumpToResult();
      }
    });

    this.listenTo(searchDNAData, 'basesChanged', () => this.doSearch());
    this.listenTo(searchDNAData, 'change:region change:searchAminoAcids change:maxMismatches', () =>
      this.doSearch()
    );
    this.showSettings();

    vo.initialize();
    this.listenTo(vo.ORFS.settings, 'change:value', () => {
      this.updateORFSearch();
    });
    this.listenTo(vo.RNA_ORFS.settings, 'change:value', () => {
      this.updateORFSearch();
    });
    this.listenTo(
      searchDNAData,
      `change:showSettings change:mode change:searchAminoAcids \
change:region`,
      () => {
        this.showSettings();
      }
    );
    this.listenTo(searchDNAData, 'change:searchAminoAcids', () => {
      this.updatePlaceHolder();
    });
    this.listenTo(searchDNAData.featureResults(), 'add remove reset', () => {
      this.showFeatureResults();
    });
    this.listenTo(searchDNAData, 'open', () => {
      this.openSearch();
    });
    this.listenTo(searchDNAData, 'close', () => {
      this.cancelSearch();
    });
    this.listenTo(searchDNAData, 'change:sequence', () => {
      this.sequenceChangeHandler();
    });
    this.listenTo(
      searchDNAData,
      `change:index reset:results change:gotoMsg change:searchMsg change:range \
change:hasSearched change:mode change:onlyShowSelected`,
      () => {
        let countText;
        let msg;
        const index = searchDNAData.get('index');
        const total = searchDNAData.get('results').length;
        const mode = searchDNAData.get('mode');
        const hasSearched = searchDNAData.get('hasSearched');
        const isCapped = searchDNAData.get('capped');
        const refine = searchDNAData.get('onlyShowSelected') ? '(refine search to see all)' : '';

        this.$('.find-next-btn').prop('disabled', index < 0);
        this.$('.find-previous-btn').prop('disabled', index < 0);

        $('.js-adv-settings').addClass('hide');
        if (mode === 'search') {
          const cutoff = hasSearched && isCapped ? '+' : '';
          const range = searchDNAData.get('range') != null ? ' (in selection)' : '';
          countText = hasSearched ? `${refine} ${index + 1} of ${total}${cutoff}${range}` : '';
          msg = searchDNAData.get('searchMsg');
          if (msg != null) {
            this.$('.seq-search-box').css('border-color', 'red');
            countText = msg;
          }
          if (hasSearched) {
            $('.seq-search-count-row').show();
          } else {
            $('.seq-search-count-row').hide();
          }
          $('.js-adv-settings').removeClass('hide');
        } else if (mode === 'gotobase') {
          msg = searchDNAData.get('gotoMsg');
          countText = msg != null ? msg : 'go to base';
        } else {
          countText = '';
        }

        this.$('.seq-search-count').text(countText);
        setTestAttrs(this.$('.seq-search-count'), {component: 'SearchDNAView', key: 'count'});
        this.$('.seq-search-box').css({'padding-right': this.$('.in-input').width()});
      }
    );
  }
}

SearchDNAView.initClass();

export default SearchDNAView;
